import { useCallback, useEffect, useState } from 'react';
import {
  readSavedThemePreference,
  readSystemTheme,
  resolveThemePreference,
  subscribeToSavedThemePreference,
  subscribeToSystemTheme,
  writeSavedThemePreference,
  type ThemePreference,
  type ThemeType,
} from '@wavvest/shared-layout';

export type AuthColorScheme = ThemeType;

const SCHEME_OVERRIDE_PARAM = 'theme';

const readSchemeOverride = (): AuthColorScheme | undefined => {
  const requested = new URLSearchParams(window.location.search).get(SCHEME_OVERRIDE_PARAM);
  return requested === 'light' || requested === 'dark' ? requested : undefined;
};

// Drop `?theme=` from the address bar so a refresh keeps the visitor's pick.
const clearSchemeOverride = () => {
  const url = new URL(window.location.href);
  if (!url.searchParams.has(SCHEME_OVERRIDE_PARAM)) {
    return;
  }
  url.searchParams.delete(SCHEME_OVERRIDE_PARAM);
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
};

/**
 * Theme for pre-auth screens. Precedence: `?theme=` override, the visitor's
 * saved choice, the firm's `default_theme` (when given), then the OS setting.
 * The `?theme=` override only sets the starting scheme: once the visitor picks
 * a theme, their choice applies and the parameter is removed from the URL.
 */
export const useAuthTheme = (defaultTheme?: AuthColorScheme | null) => {
  const [savedPreference, setSavedPreference] = useState<ThemePreference | null>(() =>
    readSavedThemePreference(),
  );
  const [systemTheme, setSystemTheme] = useState<AuthColorScheme>(() => readSystemTheme());
  const [schemeOverride, setSchemeOverride] = useState<AuthColorScheme | undefined>(() =>
    readSchemeOverride(),
  );

  useEffect(
    () => subscribeToSavedThemePreference(() => setSavedPreference(readSavedThemePreference())),
    [],
  );
  useEffect(() => subscribeToSystemTheme(() => setSystemTheme(readSystemTheme())), []);

  const setThemePreference = useCallback((preference: ThemePreference) => {
    setSchemeOverride(undefined);
    clearSchemeOverride();
    setSavedPreference(preference);
    writeSavedThemePreference(preference);
  }, []);

  const firmDefault = defaultTheme === 'light' || defaultTheme === 'dark' ? defaultTheme : null;
  const themePreference: ThemePreference = savedPreference ?? firmDefault ?? 'system';

  return {
    colorScheme: schemeOverride ?? resolveThemePreference(themePreference, systemTheme),
    setThemePreference,
    themePreference,
  };
};

/** Resolved theme plus the setter, as returned by `useAuthTheme`. */
export type AuthThemeState = ReturnType<typeof useAuthTheme>;

/**
 * The effective color scheme for pre-auth screens. Kept as a small adapter for
 * consumers that only need the resolved light/dark value.
 */
export const useAuthColorScheme = (defaultTheme?: AuthColorScheme | null): AuthColorScheme =>
  useAuthTheme(defaultTheme).colorScheme;
